import {useContext, useEffect} from "react";
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {ArrowUpRight, Check2, LightningChargeFill} from "react-bootstrap-icons";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {getProductReview, ProductReviewPayload, ReviewItem} from "../../api/reviews";

const formatPrice = (amount?: number | null) => amount
    ? `${new Intl.NumberFormat("ru-RU").format(amount / 100)} ₽`
    : null;

function imageSrc(path?: string | null) {
    if (!path) {
        return "";
    }
    if (/^https?:\/\//.test(path)) {
        return path;
    }
    return `https://www.pride.ru${path}`;
}

function anchorId(item: ReviewItem) {
    return (item.product.articul || String(item.product.id)).toLowerCase();
}

function photoLine(item: ReviewItem) {
    return item.specs
        .filter(spec => spec.onPhoto && spec.value)
        .slice(0, 2)
        .map(spec => spec.value)
        .join(" · ");
}

export default function PneumaticWrenchesReview() {
    const {slug} = useParams<{slug: string}>();
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const {data, isLoading, isError} = useQuery({
        queryKey: ["product-review", slug, state.customer?.id],
        queryFn: () => getProductReview(slug as string, state.customer),
        enabled: Boolean(slug)
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        const mainItem = data.items.find(item => item.isMain) || data.items[0];
        dispatch({
            type: ReducerActionType.SET_BREADCRUMBS,
            payload: [{name: "Обзоры"}, {name: data.title}]
        });
        dispatch({
            type: ReducerActionType.SET_PARAM,
            paramName: "meta",
            payload: {
                title: data.title,
                html_header: data.title,
                description: data.lead || "",
                image: imageSrc(mainItem?.product.image)
            }
        });

        return () => {
            dispatch({type: ReducerActionType.SET_BREADCRUMBS, payload: []});
        };
    }, [data, dispatch]);

    if (isLoading) {
        return <article className="wrench-review"><div className="container"><p>Загрузка обзора…</p></div></article>;
    }

    if (isError || !data) {
        return <article className="wrench-review"><div className="container"><p>Обзор не найден.</p></div></article>;
    }

    return <ReviewArticle review={data}/>;
}

function ReviewArticle({review}: {review: ProductReviewPayload}) {
    const mainItem = review.items.find(item => item.isMain) || review.items[0];

    return (
        <article className="wrench-review">
            <div className="container">
                <header className="wrench-review__hero">
                    <div className="wrench-review__hero-copy">
                        <div className="wrench-review__eyebrow">
                            <LightningChargeFill aria-hidden="true"/> Обзор и сравнение
                        </div>
                        <h1>{review.title}</h1>
                        {review.lead && <p className="wrench-review__lead">{review.lead}</p>}
                        {review.highlights.length > 0 && (
                            <div className="wrench-review__hero-metrics" aria-label="Параметры обзора">
                                {review.highlights.map(highlight => (
                                    <div key={highlight.label}><strong>{highlight.value}</strong><span>{highlight.label}</span></div>
                                ))}
                            </div>
                        )}
                    </div>
                    {mainItem && (
                        <a className="wrench-review__hero-product" href={mainItem.product.url}>
                            <span className="wrench-review__hero-label">Выбор обзора</span>
                            {mainItem.product.image && (
                                <img src={imageSrc(mainItem.product.image)} alt={`${mainItem.product.brand} ${mainItem.product.articul}`}/>
                            )}
                            <span className="wrench-review__hero-model">{mainItem.product.brand} <b>{mainItem.product.articul}</b></span>
                            <span className="wrench-review__hero-link">Открыть товар <ArrowUpRight aria-hidden="true"/></span>
                        </a>
                    )}
                </header>

                {review.intro.length > 0 && (
                    <section className="wrench-review__intro">
                        {review.intro.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                    </section>
                )}

                {review.items.length > 0 && (
                    <section className="wrench-review__section" aria-labelledby="quick-rating-title">
                        <div className="wrench-review__section-head">
                            <span>01</span>
                            <div>
                                <p>Сразу к результатам</p>
                                <h2 id="quick-rating-title">Краткий рейтинг</h2>
                            </div>
                        </div>
                        <div className="wrench-review__rating-grid">
                            {review.items.map(item => (
                                <a className={`wrench-review__rating-card ${item.isMain ? "is-winner" : ""}`}
                                   href={`#${anchorId(item)}`} key={item.product.id}>
                                    <span className="wrench-review__rank">{String(item.position).padStart(2, "0")}</span>
                                    <span className="wrench-review__rating-copy">
                                        <small>{item.suitability}</small>
                                        <strong>{item.product.brand} {item.product.articul}</strong>
                                        <span>{photoLine(item)}</span>
                                    </span>
                                    {item.product.image && <img src={imageSrc(item.product.image)} alt="" loading="lazy"/>}
                                </a>
                            ))}
                        </div>
                    </section>
                )}

                {review.characteristics.length > 0 && (
                    <section className="wrench-review__section" aria-labelledby="comparison-title">
                        <div className="wrench-review__section-head">
                            <span>02</span>
                            <div>
                                <p>Все цифры рядом</p>
                                <h2 id="comparison-title">Сравнение характеристик</h2>
                            </div>
                        </div>
                        <div className="wrench-review__table-wrap">
                            <table className="wrench-review__table">
                                <thead>
                                <tr>
                                    <th>Модель</th>
                                    {review.characteristics.map(characteristic => (
                                        <th key={characteristic.name}>{characteristic.label}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {review.items.map(item => (
                                    <tr key={item.product.id}>
                                        <td>
                                            <a href={`#${anchorId(item)}`}>{item.product.brand} <b>{item.product.articul}</b></a>
                                        </td>
                                        {item.specs.map((spec, index) => (
                                            <td key={spec.name}>{index === 0 && spec.value ? <strong>{spec.value}</strong> : (spec.value || "—")}</td>
                                        ))}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {review.items.length > 0 && (
                    <section className="wrench-review__section" aria-labelledby="details-title">
                        <div className="wrench-review__section-head">
                            <span>03</span>
                            <div>
                                <p>Подробный разбор</p>
                                <h2 id="details-title">Модели</h2>
                            </div>
                        </div>

                        <div className="wrench-review__products">
                            {review.items.map(item => {
                                const price = formatPrice(item.product.price?.amount);
                                const photoSpecs = item.specs.filter(spec => spec.onPhoto && spec.value);
                                return (
                                    <section className="wrench-review__product" id={anchorId(item)} key={item.product.id}>
                                        <div className="wrench-review__product-visual">
                                            <span className="wrench-review__product-rank">#{item.position}</span>
                                            {item.product.image && (
                                                <img src={imageSrc(item.product.image)} alt={`${item.product.brand} ${item.product.articul}`} loading="lazy"/>
                                            )}
                                            {photoSpecs.length > 0 && (
                                                <div className="wrench-review__product-specs">
                                                    {photoSpecs.map(spec => (
                                                        <span key={spec.name}><b>{spec.value}</b> {spec.label}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="wrench-review__product-content">
                                            <div className="wrench-review__product-heading">
                                                <div>
                                                    {item.suitability && <span className="wrench-review__badge">{item.suitability}</span>}
                                                    <h3>{item.position}. {item.product.brand} {item.product.articul}</h3>
                                                    {item.summary && <p className="wrench-review__product-subtitle">{item.summary}</p>}
                                                </div>
                                                <span className="wrench-review__brand">{item.product.brand}</span>
                                            </div>
                                            <div className="wrench-review__product-text">
                                                {item.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                                            </div>
                                            {(item.pros.length > 0 || item.cons.length > 0) && (
                                                <div className="wrench-review__pros-cons">
                                                    <div>
                                                        <strong>Плюсы</strong>
                                                        <ul>{item.pros.map(pro => <li key={pro}><Check2 aria-hidden="true"/>{pro}</li>)}</ul>
                                                    </div>
                                                    <div className="is-cons">
                                                        <strong>Минусы</strong>
                                                        <ul>{item.cons.map(con => <li key={con}><span>−</span>{con}</li>)}</ul>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="wrench-review__product-cta">
                                                <a className="btn btn-primary big" href={item.product.url}>
                                                    Перейти к товару <ArrowUpRight aria-hidden="true"/>
                                                </a>
                                                {price && <span><small>Цена на сайте</small><b>{price}</b></span>}
                                                {item.product.stockLabel && (
                                                    <span className={item.product.hasStock ? "is-available" : ""}>
                                                        {item.product.stockLabel}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    </section>
                )}

                {(review.conclusion.cards.length > 0 || review.conclusion.note) && (
                    <section className="wrench-review__choice" aria-labelledby="choice-title">
                        <div className="wrench-review__section-head wrench-review__section-head--light">
                            <span>04</span>
                            <div>
                                <p>Финальный выбор</p>
                                <h2 id="choice-title">Какой выбрать</h2>
                            </div>
                        </div>
                        {review.conclusion.cards.length > 0 && (
                            <div className="wrench-review__choice-grid">
                                {review.conclusion.cards.map(card => (
                                    <p key={card.title} className={card.isMain ? "is-main" : ""}>
                                        <b>{card.title}</b><span>{card.caption}</span><strong>{card.value}</strong>
                                    </p>
                                ))}
                            </div>
                        )}
                        {review.conclusion.note && <p className="wrench-review__choice-note">{review.conclusion.note}</p>}
                    </section>
                )}
            </div>
        </article>
    );
}
